import React from 'react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, HStack, useMediaQuery } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import MobileManageTab from 'components/MobileManageTab'
import { MOBILE_MEDIA_QUERY } from 'constants/mobile-page-template'
import SvgIcon from 'components/SvgIcon'
import routes from 'routes'
import { DARK_GREY, DARK_PAGE_BG, DARK_TABLE_BORDER, DARK_TEXT_MUTED } from 'constants/dark-tokens'
import { AUTH_MOBILE_ACCENT } from 'components/Layouts/AuthLayout/constants'

interface PlayPageHeaderProps {
  // Breadcrumb tail rendered after the home icon + first chevron (e.g. "Plans"
  // or "Plans › {name}"). The leading home/chevron are owned by this component.
  breadcrumb: React.ReactNode
  showManage?: boolean
  isManageActive?: boolean
  onToggleManage?: () => void
}

// Shared dark page header (RallyPD chrome). Single source of truth for the
// breadcrumb + Manage/Done pill so the plans list and the practice-detail page
// render the exact same header instead of two look-alikes.
const PlayPageHeader: React.FC<PlayPageHeaderProps> = ({ breadcrumb, showManage, isManageActive, onToggleManage }) => {
  const navigate = useNavigate()
  const [isMobile] = useMediaQuery(MOBILE_MEDIA_QUERY)

  // The pill below is a desktop control; on a phone the same toggle is this tab
  // on the right edge, which has room for an icon where a 402px row has none.
  const manageTab =
    showManage && onToggleManage ? (
      <MobileManageTab isManageMode={Boolean(isManageActive)} onToggle={onToggleManage} />
    ) : null

  // A main page draws no breadcrumb row on a phone: the topbar above and the nav
  // bar below already say where the viewer is, and this is a page-name row on a
  // screen that has none to spare. Only the Manage tab survives.
  if (isMobile) return manageTab

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        px="16px"
        py="16px"
        gap="12px"
        bg={DARK_PAGE_BG}
        borderBottom={`1px solid ${DARK_TABLE_BORDER}`}
      >
        <HStack spacing="8px" overflow="hidden" flex={1}>
          <Box
            as="span"
            cursor="pointer"
            onClick={() => navigate(routes.dashboard.value, { replace: true })}
            display="inline-flex"
            flexShrink={0}
          >
            <SvgIcon iconName="home" size={20} color={AUTH_MOBILE_ACCENT} />
          </Box>
          <ChevronRightIcon color={DARK_TEXT_MUTED} flexShrink={0} />
          {breadcrumb}
        </HStack>
        {/* The pill is a desktop control; on a phone the same toggle is the edge
          tab below, which has room for an icon where this has none. */}
        {showManage && onToggleManage && (
          <Button
            onClick={onToggleManage}
            display={{ base: 'none', md: 'inline-flex' }}
            h="32px"
            px="16px"
            flexShrink={0}
            borderRadius="full"
            bg="transparent"
            color="#F2F7FA"
            border={`1px solid ${DARK_GREY}`}
            fontSize="14px"
            fontWeight={500}
            _hover={{ bg: 'rgba(255, 255, 255, 0.06)' }}
            _active={{ bg: 'rgba(255, 255, 255, 0.1)' }}
            _focusVisible={{ boxShadow: 'none' }}
            leftIcon={isManageActive ? <SvgIcon iconName="check-circle" size={16} color="#F2F7FA" /> : undefined}
          >
            {isManageActive ? 'Done' : 'Manage'}
          </Button>
        )}
      </Flex>
      {manageTab}
    </>
  )
}

export default PlayPageHeader
